import type { Flags } from 'flagpack-core'

/** Maps ISO 639 language codes to ISO 3166-1 alpha-2 country codes for flagpack. */
const LANGUAGE_TO_FLAG: Record<string, Flags> = {
  is: 'IS',
  de: 'DE',
  no: 'NO',
  nb: 'NO',
  nn: 'NO',
  da: 'DK',
  sv: 'SE',
  fi: 'FI',
  es: 'ES',
  it: 'IT',
  pl: 'PL',
  uk: 'UA',
  pt: 'PT',
  fr: 'FR',
  en: 'GB-UKM',
  ja: 'JP',
  ko: 'KR',
  zh: 'CN',
  ar: 'SA',
  hi: 'IN',
  ru: 'RU',
  tr: 'TR',
  nl: 'NL',
  el: 'GR',
  cs: 'CZ',
  ro: 'RO',
  hu: 'HU',
  hr: 'HR',
}

/** Languages that use custom flags (not in flagpack-core). */
const LANGUAGE_TO_CUSTOM_FLAG: Record<string, string> = {
  la: 'ROMAN',
}

export function languageToFlagCode(isoCode: string): Flags | null {
  const key = isoCode.toLowerCase()
  if (key in LANGUAGE_TO_CUSTOM_FLAG) return null
  return LANGUAGE_TO_FLAG[key] ?? null
}

export function languageToCustomFlagCode(isoCode: string): string | null {
  return LANGUAGE_TO_CUSTOM_FLAG[isoCode.toLowerCase()] ?? null
}

// Lazy glob — Vite only emits the SVGs that are actually resolved at runtime,
// and each flag is loaded on demand rather than all ~256 per size upfront.
type FlagLoader = () => Promise<{ default: string }>
const flagLoaders: Record<string, Record<string, FlagLoader>> = {
  s: import.meta.glob('@flags/s/*.svg', { query: '?url' }) as Record<string, FlagLoader>,
  m: import.meta.glob('@flags/m/*.svg', { query: '?url' }) as Record<string, FlagLoader>,
  l: import.meta.glob('@flags/l/*.svg', { query: '?url' }) as Record<string, FlagLoader>,
}

const customFlagLoaders: Record<string, Record<string, FlagLoader>> = {
  s: import.meta.glob('@/assets/flags/s/*.svg', { query: '?url' }) as Record<string, FlagLoader>,
  m: import.meta.glob('@/assets/flags/m/*.svg', { query: '?url' }) as Record<string, FlagLoader>,
  l: import.meta.glob('@/assets/flags/l/*.svg', { query: '?url' }) as Record<string, FlagLoader>,
}

// Cache resolved URLs so each flag is only loaded once.
const resolvedUrls = new Map<string, string>()

async function loadFromLoaders(loaders: Record<string, FlagLoader>, code: string): Promise<string | null> {
  const suffix = `/${code}.svg`
  for (const [key, loader] of Object.entries(loaders)) {
    if (key.endsWith(suffix)) {
      const mod = await loader()
      return mod.default
    }
  }
  return null
}

export async function getFlagUrl(code: string, size: 's' | 'm' | 'l'): Promise<string | null> {
  const cacheKey = `${size}:${code}`
  const cached = resolvedUrls.get(cacheKey)
  if (cached) return cached

  const url = await loadFromLoaders(flagLoaders[size] ?? {}, code)
    ?? await loadFromLoaders(customFlagLoaders[size] ?? {}, code)

  if (url) {
    resolvedUrls.set(cacheKey, url)
    return url
  }
  return null
}
